import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { ChatMessage } from '../../types';
import { chatService } from '../../services/chatService';
import { v4 as uuidv4 } from 'uuid';
import Composer from './Composer';

export interface CitationData {
  file_name?: string;
  fund?: string;
  as_of_date?: string;
}

interface ChatProps {
  onCitationClick: (citationId: string, page: number, citation?: CitationData) => void;
  onToggleSidebar?: () => void;
  conversationTitle?: string;
}

export interface ChatRef {
  handleThreadSelect: (threadId: string, messages: any[]) => void;
}

const Chat = forwardRef<ChatRef, ChatProps>(({
  onCitationClick,
  onToggleSidebar,
  conversationTitle
}, ref) => {
  const [currentThreadMessages, setCurrentThreadMessages] = useState<ChatMessage[]>([]);
  const [sending, setSending] = useState(false);
  const [currentThreadId, setCurrentThreadId] = useState<string>('');
  const [threadId, setThreadId] = useState(() => uuidv4());
  const [pollingInterval, setPollingInterval] = useState<ReturnType<typeof setInterval> | null>(null);

  // Determine chat title
  const chatTitle = conversationTitle || (currentThreadMessages.length > 0 ? currentThreadMessages[0].question.slice(0, 50) : 'New Chat');

  const startPolling = (targetThreadId?: string) => {
    const targetId = targetThreadId || currentThreadId;
    if (!targetId) { console.log('No thread ID found'); return; }
    console.log('Starting polling for thread:', targetId);
    
    if (pollingInterval) {
      clearInterval(pollingInterval);
    }

    (async () => {
      try {
        const response = await chatService.getThreadMessages(targetId);
        const data = await response.data;
        if (data.conversations) {
          setCurrentThreadMessages(data.conversations);
        }
      } catch (error) {
        console.error('Initial polling fetch error:', error);
      }
    })();
    
    const interval = setInterval(async () => {
      try {
        const response = await chatService.getThreadMessages(targetId);
        const data = await response.data;
        
        if (data.conversations) {
          setCurrentThreadMessages(data.conversations);
        }
      } catch (error) {
        console.error('Polling error:', error);
      }
    }, 5000);
    
    setPollingInterval(interval);
  };

  const stopPolling = () => {
    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
  };

  const handleSendMessage = async (userMessage: string) => {
    if (!userMessage.trim()) return;

    const trimmedMessage = userMessage.trim();
    setSending(true);

    const newMessage: ChatMessage = {
      question: trimmedMessage,
      answer: '',
      citations: []
    };
    setCurrentThreadMessages(prev => [...prev, newMessage]);

    try {
      let response;
      if (currentThreadMessages.length > 0 && currentThreadId) {
        console.log('Sending to existing thread:', currentThreadId);
        response = await chatService.sendMessageToThread(trimmedMessage, currentThreadId);
        console.log('Response done:', response);
        startPolling(currentThreadId);
      } else {
        console.log('Creating new thread:', threadId);
        response = await chatService.sendMessage(trimmedMessage, threadId);
        console.log('Response done:', response);
        let responseThreadId = response.thread_id;
        console.log('Response thread ID:', responseThreadId);
        if (responseThreadId) {
          setCurrentThreadId(responseThreadId);
          setTimeout(() => {
            startPolling(responseThreadId);
          }, 5000);
        }
      }
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setSending(false);
    }
  };

  const handleClearChat = () => {
    stopPolling();
    setCurrentThreadMessages([]);
    setCurrentThreadId('');
  };

  // Render text with clickable inline citations
  const renderTextWithClickableCitations = (text: string, citations: any[]) => {
    const citationPattern = /\[(\d+(?:,\s*\d+)*)\]/g;
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = citationPattern.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push(text.substring(lastIndex, match.index));
      }

      const matchIndex = match.index;
      const citationNumbers = match[1].split(',').map(n => n.trim());
      const citationElements: React.ReactNode[] = [];

      citationNumbers.forEach((num, idx) => {
        const citation = citations.find(c => c.chunk_index?.toString().trim() === num.trim());

        if (idx > 0) {
          citationElements.push(
            <span key={`sep-${matchIndex}-${idx}`}>, </span>
          );
        }

        citationElements.push(
          <button
            key={`btn-${matchIndex}-${num}`}
            className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium transition-colors cursor-pointer hover:underline"
            onClick={(e) => {
              e.preventDefault();
              console.warn('[Chat] Citation clicked (inline)', citation?.id, 'onCitationClick:', !!onCitationClick);
              if (citation && onCitationClick) {
                const pageValue = Array.isArray(citation.page) ? citation.page[0] : citation.page;
                const pageNum = parseInt(String(pageValue)) || 1;
                onCitationClick(citation.id, pageNum, {
                  file_name: citation.file_name,
                  fund: citation.fund,
                  as_of_date: citation.as_of_date
                });
              }
            }}
          >
            {num}
          </button>
        );
      });

      parts.push(
        <span key={matchIndex} className="inline-flex items-center gap-0.5">
          <span>[</span>
          {citationElements}
          <span>]</span>
        </span>
      );

      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < text.length) {
      parts.push(text.substring(lastIndex));
    }

    return parts.length > 0 ? parts : text;
  };

  const handleThreadSelect = (threadId: string, messages: any[]) => {
    stopPolling();
    setCurrentThreadMessages(messages);
    setCurrentThreadId(threadId);

    // If clearing chat (empty threadId and no messages), generate a new threadId
    if (!threadId && messages.length === 0) {
      setThreadId(uuidv4());
    }

    if (messages.length > 0) {
      startPolling(threadId);
    }
  };

  useImperativeHandle(ref, () => ({
    handleThreadSelect
  }));

  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, []);

  return (
    <div className="flex flex-col h-full">
      {/* Header with hamburger */}
      <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-3">
        {onToggleSidebar && (
          <button
            onClick={onToggleSidebar}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title="Toggle sidebar"
          >
            <svg className="h-5 w-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        )}
        <div className="flex-1"></div>
        {currentThreadMessages.length > 0 && (
          <button
            onClick={handleClearChat}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title="Clear chat"
          >
            <svg className="h-5 w-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Chat content */}
      <div className="flex-1 overflow-hidden flex flex-col px-4 py-2 max-h-[calc(100vh-220px)]">
        <div className="flex-1 bg-gray-50 rounded-lg p-4 overflow-y-auto mb-2">
          {currentThreadMessages.length > 0 ? (
            <div className="space-y-4">
              {currentThreadMessages.map((msg, index) => (
                <div key={index} className="space-y-2">
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <div className="font-medium text-blue-800">Q: {msg.question}</div>
                  </div>
                  <div className="bg-white p-3 rounded-lg border">
                    <div className="text-gray-800">
                      A: {msg.answer ? (
                        <div className="prose prose-sm max-w-none">
                          <ReactMarkdown
                            components={{
                              p: ({ children }) => {
                                const processedChildren = React.Children.map(children, (child) => {
                                  if (typeof child === 'string') {
                                    return renderTextWithClickableCitations(child, msg.citations || []);
                                  }
                                  return child;
                                });
                                return <p>{processedChildren}</p>;
                              },
                              li: ({ children }) => {
                                const processedChildren = React.Children.map(children, (child) => {
                                  if (typeof child === 'string') {
                                    return renderTextWithClickableCitations(child, msg.citations || []);
                                  }
                                  return child;
                                });
                                return <li>{processedChildren}</li>;
                              }
                            }}
                          >
                            {msg.answer}
                          </ReactMarkdown>
                        </div>
                      ) : (
                        <span className="flex items-center text-gray-500">
                          <span className="animate-pulse">Processing...</span>
                          <div className="ml-2 animate-spin rounded-full h-4 w-4 border-b-2 border-adalabs-blue-500"></div>
                        </span>
                      )}
                    </div>
                    {msg.citations && msg.citations.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-gray-200">
                        <div className="text-sm text-gray-600">
                          Citations: {msg.citations.map((citation: any, idx: number) => {
                            const pageValue = Array.isArray(citation.page) ? citation.page[0] : citation.page;
                            const pageNum = parseInt(String(pageValue)) || 1;

                            return (
                              <span
                                key={idx}
                                className="inline-block bg-blue-100 text-blue-800 px-2 py-1 rounded mr-1 mb-1 cursor-pointer hover:bg-blue-200 transition-colors"
                                onClick={() => {
                                  console.warn('[Chat] Citation clicked (list)', citation.id);
                                  onCitationClick(citation.id, pageNum, {
                                  file_name: citation.file_name,
                                  fund: citation.fund,
                                  as_of_date: citation.as_of_date
                                  });
                                }}
                              >
                                {citation.chunk_index}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-gray-500 text-center">Chat messages will appear here</div>
          )}
        </div>
      </div>

      {/* Composer at the bottom */}
      <Composer
        onSendMessage={handleSendMessage}
        isStreaming={sending}
        disabled={sending}
        placeholder="Send a message..."
        showTokenCount={false}
      />
    </div>
  );
});

Chat.displayName = 'Chat';

export default Chat;
